import { c as _c } from "react/compiler-runtime";
import { Button, Tooltip, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import type { Condition, Provider } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { isValueCondition, PropertyType, type ConditionPanelHost, type FilterProperty } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { SearchTextBox } from '../../search/searchTextBox';
import { MultiSelectFilter, type FilterProps } from '../filters';
import { TAG_KEYS_PROPERTY, TAG_KEY_VALUES_PROPERTY } from '../propertyNames';

/**
 * TagsFilter is dedicated to the resource-tags filter, so it owns the mapping
 * between the selected filter mode and the property the condition is written
 * against: key-only selections must target `tagKeys` and key=value selections
 * `tagKeyValues`, otherwise the backend filters on the wrong column (EON-12659).
 * The mapping lives here (rather than importing the property objects from
 * properties.tsx) because properties.tsx imports this module — referencing it
 * back would create a circular dependency.
 */
const TAG_FILTER_PROPERTY_BY_MODE: Record<'key' | 'key-value', FilterProperty> = {
  key: {
    name: TAG_KEYS_PROPERTY,
    displayName: 'Tag keys',
    type: PropertyType.MultipleValues,
    group: 'primary'
  },
  'key-value': {
    name: TAG_KEY_VALUES_PROPERTY,
    displayName: 'Tag key-value pairs',
    type: PropertyType.MultipleValues,
    group: 'primary'
  }
};
export interface TagsFilterProps extends FilterProps {
  supportedProviders?: Provider[];
  onDoneClick: () => void;
  initialFilterMode: 'key' | 'value' | 'key-value';
  panelHost?: ConditionPanelHost;
  dataSource?: 'tags' | 'sourceAccountTags';
}
export interface TagsDataResult {
  data: {
    tags?: {
      key: string;
      value?: string | null;
    }[];
  } | undefined;
  isLoading: boolean;
}
export function useResourceTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.tags.useList(filter);
}
export function useSourceAccountTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.sourceAccountTags.useList(filter);
}
const ResourceTagsFilterWrapper = props => {
  const $ = _c(3);
  const tagsData = useResourceTagsData();
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsFilterContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const SourceAccountTagsFilterWrapper = props => {
  const $ = _c(3);
  const tagsData = useSourceAccountTagsData();
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsFilterContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const TagsFilter = props => {
  const $ = _c(4);
  if (props.dataSource === "sourceAccountTags") {
    let t0;
    if ($[0] !== props) {
      t0 = <SourceAccountTagsFilterWrapper {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <ResourceTagsFilterWrapper {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const TagsFilterContent = props => {
  const $ = _c(64);
  const [keyFilter, setKeyFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  const [filterMode, setFilterMode] = useState(props.initialFilterMode);
  const {
    data: body,
    isLoading
  } = props.tagsData;
  const activeProperty = TAG_FILTER_PROPERTY_BY_MODE[filterMode === "key" ? "key" : "key-value"];
  let t0;
  if ($[0] !== activeProperty || $[1] !== props.conditions) {
    let t1;
    if ($[3] !== activeProperty) {
      t1 = x => isValueCondition(x) && x.property === activeProperty.name;
      $[3] = activeProperty;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = props.conditions.find(t1);
    $[0] = activeProperty;
    $[1] = props.conditions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[5] !== condition) {
    t1 = isValueCondition(condition) && condition?.value || [];
    $[5] = condition;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const selectedResources = t1;
  let t2;
  if ($[7] !== body?.tags) {
    t2 = body?.tags || [];
    $[7] = body?.tags;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const tags = t2;
  selectedResources.forEach(tag => {
    const [key, value] = tag.split("=");
    if (!tags.some(t => t.key === key && t.value === value)) {
      tags.push({
        key,
        value
      });
    }
  });
  const allTags = tags;
  let t3;
  if ($[9] !== allTags) {
    t3 = allTags.reduce(_temp, {
      keys: new Set(),
      values: new Set()
    });
    $[9] = allTags;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const tagsReduced = t3;
  let t4;
  if ($[11] !== keyFilter || $[12] !== tagsReduced.keys) {
    t4 = Array.from(tagsReduced.keys).filter(key_0 => !keyFilter || key_0.toLowerCase().includes(keyFilter.toLowerCase())).map(_temp2);
    $[11] = keyFilter;
    $[12] = tagsReduced.keys;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const keys = t4;
  let t5;
  if ($[14] !== tagsReduced.values || $[15] !== valueFilter) {
    t5 = Array.from(tagsReduced.values).filter(value_0 => !valueFilter || value_0.toLowerCase().includes(valueFilter.toLowerCase())).sort().map(_temp3);
    $[14] = tagsReduced.values;
    $[15] = valueFilter;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const values = t5;
  let t6;
  if ($[17] !== allTags || $[18] !== keyFilter || $[19] !== valueFilter) {
    let t7;
    if ($[21] !== keyFilter || $[22] !== valueFilter) {
      t7 = t_0 => (!keyFilter || t_0.key.toLowerCase().includes(keyFilter.toLowerCase())) && (!valueFilter || !!t_0.value?.toLowerCase().includes(valueFilter.toLowerCase()));
      $[21] = keyFilter;
      $[22] = valueFilter;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    const allValues = Array.from(new Set(allTags.filter(t7).map(_temp4)));
    t6 = allValues.map(_temp5);
    $[17] = allTags;
    $[18] = keyFilter;
    $[19] = valueFilter;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const keyValuePairs = t6;
  if (isLoading) {
    let t7;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Loader />;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    return t7;
  }
  const options = filterMode === "key" ? keys : filterMode === "value" ? values : keyValuePairs;
  let t7;
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      gap: "12px"
    };
    t8 = <Typography>Filter by</Typography>;
    $[25] = t7;
    $[26] = t8;
  } else {
    t7 = $[25];
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event => setFilterMode(event.target.value);
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <MenuItem value="key">Tag keys</MenuItem>;
    t11 = <MenuItem value="key-value">Tag key-value pairs</MenuItem>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t10 = $[28];
    t11 = $[29];
  }
  let t12;
  if ($[30] !== filterMode) {
    t12 = <Select value={filterMode} onChange={t9} data-testid="tags-filter-mode">{t10}{t11}</Select>;
    $[30] = filterMode;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = `To filter your resources, select one or more ${filterMode}${filterMode === "key" || filterMode === "value" ? "s" : " pairs"}.`;
  let t14;
  if ($[32] !== t13) {
    t14 = <Typography className="my-[12px]" variant="body1">{t13}</Typography>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== filterMode || $[35] !== keyFilter || $[36] !== valueFilter) {
    t15 = (filterMode.includes("key") || filterMode.includes("value")) && <Stack direction="row" className="w-full" sx={{
      alignItems: "center",
      gap: "12px"
    }}><Stack sx={{
        gap: "12px",
        flexGrow: 1
      }}>{filterMode.includes("key") && <SearchTextBox value={keyFilter} onChange={setKeyFilter} placeholder="Search by key" />}{filterMode.includes("value") && <SearchTextBox value={valueFilter} onChange={setValueFilter} placeholder="Search by value" />}</Stack></Stack>;
    $[34] = filterMode;
    $[35] = keyFilter;
    $[36] = valueFilter;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t12 || $[39] !== t14 || $[40] !== t15) {
    t16 = <ScrollableHeader className="px-[24px] pb-[24px] py-[12px]" sx={t7}>{t8}{t12}{t14}{t15}</ScrollableHeader>;
    $[38] = t12;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== options.length) {
    t17 = options.length === 0 && <Typography variant="body1" className="my-[12px]">{"No matching values found. Try a new search term."}</Typography>;
    $[42] = options.length;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== activeProperty || $[45] !== keyFilter || $[46] !== options || $[47] !== props || $[48] !== valueFilter) {
    t18 = options.length > 0 && <MultiSelectFilter {...props} property={activeProperty} showSelectAll={!!keyFilter || !!valueFilter} options={options} />;
    $[44] = activeProperty;
    $[45] = keyFilter;
    $[46] = options;
    $[47] = props;
    $[48] = valueFilter;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t17 || $[51] !== t18) {
    t19 = <ScrollableContent className="px-[24px] py-[12px]">{t17}{t18}</ScrollableContent>;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t16 || $[54] !== t19) {
    t20 = <ScrollableWrapper>{t16}{t19}</ScrollableWrapper>;
    $[53] = t16;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== condition || $[57] !== filterMode || $[58] !== props || $[59] !== selectedResources.length) {
    t21 = selectedResources.length > 0 && <Stack direction="row" className="px-[24px] py-[16px]" sx={{
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "var(--mui-customShadows-panelUp)"
    }}><Typography>{`${selectedResources.length} ${pluralize(filterMode === "key" || filterMode === "value" ? filterMode : "pair", selectedResources.length)} selected`}</Typography><Stack direction="row" sx={{
        alignItems: "center"
      }}><Tooltip title="Clear all"><IconButton onClick={() => props.onChange(props.conditions.filter(x_1 => x_1 !== condition))}><i className="material-symbols-filter-alt-off-outline-rounded text-(--mui-palette-text-primary) h-[20px] w-[20px]" /></IconButton></Tooltip><Button className="ml-[12px]" variant="outlined" onClick={() => props.onDoneClick()}>Done</Button></Stack></Stack>;
    $[56] = condition;
    $[57] = filterMode;
    $[58] = props;
    $[59] = selectedResources.length;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t20 || $[62] !== t21) {
    t22 = <Stack className="h-full overflow-hidden">{t20}{t21}</Stack>;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  return t22;
};
function _temp(acc, tag_0) {
  acc.keys.add(tag_0.key);
  if (tag_0.value) {
    acc.values.add(tag_0.value);
  }
  return acc;
}
function _temp2(key_1) {
  return {
    id: key_1,
    name: key_1
  };
}
function _temp3(value_1) {
  return {
    id: value_1,
    name: value_1
  };
}
function _temp4(x_0) {
  return `${x_0.key}=${x_0.value || ""}`;
}
function _temp5(value_2) {
  return {
    id: value_2,
    name: value_2
  };
}